import { PushNotifications } from "@capacitor/push-notifications";
import { Platform } from "./platform";

export interface PushPermissionResult {
  receive: "granted" | "denied" | "prompt" | "prompt-with-rationale";
}

export interface PushToken {
  value: string;
}

export interface PushNotificationSchema {
  id?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  data: Record<string, unknown>;
}

export interface PushNotificationActionPerformed {
  notification: PushNotificationSchema;
  actionId: string;
  inputValue?: string;
}

export interface PushNotificationListenerHandle {
  remove: () => Promise<void>;
}

const NO_OP_LISTENER_HANDLE: PushNotificationListenerHandle = {
  remove: async () => {
    // No-op for web platform
  },
};

/**
 * Checks the current push notification permission status without prompting the user.
 * On web, returns denied.
 */
export async function checkPushPermissions(): Promise<PushPermissionResult> {
  if (!Platform.isMobile) {
    return { receive: "denied" };
  }

  return await PushNotifications.checkPermissions();
}

/**
 * Requests push notification permissions.
 * On native platforms, prompts the user for notification permissions.
 * On web, returns denied.
 */
export async function requestPushPermissions(): Promise<PushPermissionResult> {
  if (!Platform.isMobile) {
    return { receive: "denied" };
  }

  return await PushNotifications.requestPermissions();
}

/**
 * Registers the device for push notifications.
 * On native platforms, registers with APNs/FCM.
 * On web, this is a no-op.
 */
export async function registerPushNotifications(): Promise<void> {
  if (!Platform.isMobile) {
    return;
  }

  await PushNotifications.register();
}

/**
 * Adds a listener for push notification registration success.
 * On native platforms, listens for registration token events.
 * On web, returns a no-op listener handle.
 */
export async function addPushRegistrationListener(
  callback: (token: PushToken) => void
): Promise<PushNotificationListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  return await PushNotifications.addListener("registration", callback);
}

/**
 * Adds a listener for push notification registration errors.
 * On native platforms, listens for registration error events.
 * On web, returns a no-op listener handle.
 */
export async function addPushRegistrationErrorListener(
  callback: (error: unknown) => void
): Promise<PushNotificationListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  return await PushNotifications.addListener("registrationError", callback);
}

/**
 * Adds a listener for received push notifications (foreground).
 * On native platforms, listens for push notifications received while app is in foreground.
 * On web, returns a no-op listener handle.
 */
export async function addPushNotificationReceivedListener(
  callback: (notification: PushNotificationSchema) => void
): Promise<PushNotificationListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  return await PushNotifications.addListener("pushNotificationReceived", callback);
}

/**
 * Adds a listener for push notification actions performed.
 * On native platforms, listens for when the user taps on a notification.
 * On web, returns a no-op listener handle.
 */
export async function addPushNotificationActionPerformedListener(
  callback: (action: PushNotificationActionPerformed) => void
): Promise<PushNotificationListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  return await PushNotifications.addListener("pushNotificationActionPerformed", callback);
}
